import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  AlertTriangle,
  X,
  Info,
  CheckCircle2,
  AlertCircle,
  Undo2,
} from 'lucide-react'

interface ConfirmOptions {
  title: string
  message?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'danger' | 'default'
  /**
   * When set, the user must type this exact word (case-insensitive) before the
   * confirm button is enabled — used for irreversible actions like deleting a
   * whole project.
   */
  requirePhrase?: string
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn>(async () => false)

/** Hook: `const confirm = useConfirm(); if (await confirm({...})) { ... }` */
export function useConfirm(): ConfirmFn {
  return useContext(ConfirmContext)
}

/** Notify (single-OK) dialog — drop-in replacement for window.alert() with
 *  app-consistent visuals. Tones drive the icon + colour. Returns a promise
 *  that resolves when the user dismisses (click OK, close button, Esc,
 *  backdrop). */
export interface NotifyOptions {
  title: string
  message?: ReactNode
  tone?: 'info' | 'success' | 'warning' | 'error'
  okLabel?: string
}
type NotifyFn = (opts: NotifyOptions) => Promise<void>
const NotifyContext = createContext<NotifyFn>(async () => {})
export function useNotify(): NotifyFn {
  return useContext(NotifyContext)
}

/** Undo toast — a non-modal bottom toast with a countdown bar. The action
 *  is DEFERRED: `onCommit` fires when the grace window elapses, `onUndo`
 *  fires if the user clicks « Annuler » first. Showing a second toast while
 *  one is pending commits the first immediately. */
export interface UndoToastOptions {
  message: ReactNode
  /** Grace window before commit (ms). Default 15000. */
  durationMs?: number
  onCommit: () => void
  onUndo?: () => void
  undoLabel?: string
}
type UndoToastFn = (opts: UndoToastOptions) => void
const UndoToastContext = createContext<UndoToastFn>(() => {})
export function useUndoToast(): UndoToastFn {
  return useContext(UndoToastContext)
}

interface PendingState {
  opts: ConfirmOptions
  resolve: (value: boolean) => void
}
interface PendingNotifyState {
  opts: NotifyOptions
  resolve: () => void
}

const NOTIFY_ICON = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: AlertCircle,
} as const
const NOTIFY_CHIP = {
  info: 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]',
  success:
    'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]',
  warning: 'bg-amber-100 text-amber-700',
  error: 'bg-[var(--color-danger-bg)] text-[var(--color-danger)]',
} as const

/** Provides a promise-based confirm() backed by a styled, app-consistent modal. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const [pending, setPending] = useState<PendingState | null>(null)
  const [pendingNotify, setPendingNotify] = useState<PendingNotifyState | null>(
    null,
  )
  const [phrase, setPhrase] = useState('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      setPhrase('')
      setPending({ opts, resolve })
    })
  }, [])

  const notify = useCallback<NotifyFn>((opts) => {
    return new Promise<void>((resolve) => {
      setPendingNotify({ opts, resolve })
    })
  }, [])

  // ── Undo toast ───────────────────────────────────────────────────────
  const [undoToast, setUndoToast] = useState<
    (UndoToastOptions & { id: number }) | null
  >(null)
  const undoRef = useRef<{ opts: UndoToastOptions; timer: number } | null>(null)

  const showUndoToast = useCallback<UndoToastFn>((opts) => {
    // Commit any in-flight toast before replacing it.
    const cur = undoRef.current
    if (cur) {
      window.clearTimeout(cur.timer)
      undoRef.current = null
      cur.opts.onCommit()
    }
    const dur = opts.durationMs ?? 15000
    const id = Date.now()
    const timer = window.setTimeout(() => {
      undoRef.current = null
      setUndoToast(null)
      opts.onCommit()
    }, dur)
    undoRef.current = { opts, timer }
    setUndoToast({ ...opts, id })
  }, [])

  const handleUndo = useCallback(() => {
    const cur = undoRef.current
    if (!cur) return
    window.clearTimeout(cur.timer)
    undoRef.current = null
    setUndoToast(null)
    cur.opts.onUndo?.()
  }, [])

  // Flush a pending commit if the provider unmounts (e.g. full reload).
  useEffect(() => {
    return () => {
      const cur = undoRef.current
      if (cur) {
        window.clearTimeout(cur.timer)
        undoRef.current = null
        cur.opts.onCommit()
      }
    }
  }, [])

  const settle = useCallback(
    (result: boolean) => {
      if (!pending) return
      pending.resolve(result)
      setPending(null)
    },
    [pending],
  )
  const settleNotify = useCallback(() => {
    if (!pendingNotify) return
    pendingNotify.resolve()
    setPendingNotify(null)
  }, [pendingNotify])

  useEffect(() => {
    if (!pending && !pendingNotify) return
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      if (pendingNotify) settleNotify()
      else if (pending) settle(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pending, pendingNotify, settle, settleNotify])

  useEffect(() => {
    if (!pending?.opts.requirePhrase) return
    const t = setTimeout(() => inputRef.current?.focus(), 30)
    return () => clearTimeout(t)
  }, [pending])

  const opts = pending?.opts
  const phraseRequired = opts?.requirePhrase?.trim() ?? ''
  const phraseOk =
    !phraseRequired ||
    phrase.trim().toLowerCase() === phraseRequired.toLowerCase()
  const tone = opts?.tone ?? 'danger'

  return (
    <ConfirmContext.Provider value={confirm}>
    <NotifyContext.Provider value={notify}>
    <UndoToastContext.Provider value={showUndoToast}>
      {children}

      {/* Undo toast — non-modal, bottom-centre, with a countdown bar. */}
      {undoToast && (
        <div
          key={undoToast.id}
          className="fixed bottom-5 left-1/2 z-[70] -translate-x-1/2"
          role="status"
        >
          <div className="overflow-hidden rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-text-strong)] text-white shadow-[var(--shadow-modal)]">
            <div className="flex items-center gap-3 px-4 py-2.5">
              <span className="text-[12.5px] font-medium">
                {undoToast.message}
              </span>
              <button
                type="button"
                onClick={handleUndo}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-white/15 px-2.5 py-1 text-[12px] font-semibold transition-colors hover:bg-white/25"
              >
                <Undo2 className="h-3.5 w-3.5" />
                {undoToast.undoLabel ?? t('common.undo')}
              </button>
            </div>
            {/* Countdown bar — drains over the grace window. */}
            <div className="h-1 w-full bg-white/15">
              <div
                className="animate-undo-shrink h-full bg-[var(--color-accent)]"
                style={
                  {
                    '--undo-ms': `${undoToast.durationMs ?? 15000}ms`,
                  } as React.CSSProperties
                }
              />
            </div>
          </div>
        </div>
      )}
      {pendingNotify && (() => {
        const n = pendingNotify.opts
        const tone = n.tone ?? 'info'
        const Icon = NOTIFY_ICON[tone]
        return (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
            onClick={settleNotify}
          >
            <div
              className="w-full max-w-md overflow-hidden rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3 px-6 pt-5">
                <div
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl',
                    NOTIFY_CHIP[tone],
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <h3 className="text-[15px] font-semibold text-[var(--color-text-strong)]">
                    {n.title}
                  </h3>
                  {n.message && (
                    <div className="mt-1 text-[12.5px] leading-relaxed text-[var(--color-text)]">
                      {n.message}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={settleNotify}
                  className="rounded-xl p-1.5 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
                  aria-label={t('common.close')}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-4 flex justify-end border-t border-[var(--color-border-soft)] bg-[var(--color-surface-2)]/40 px-6 py-3">
                <button
                  type="button"
                  onClick={settleNotify}
                  autoFocus
                  className="rounded-xl bg-[var(--color-accent)] px-4 py-2 text-[12.5px] font-semibold text-white shadow-[var(--shadow-soft)] transition-colors hover:bg-[var(--color-accent-hover)]"
                >
                  {n.okLabel ?? t('common.ok')}
                </button>
              </div>
            </div>
          </div>
        )
      })()}
      {pending && opts && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm"
          onClick={() => settle(false)}
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start gap-3 px-6 pt-5">
              <div
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl',
                  tone === 'danger'
                    ? 'bg-[var(--color-danger-bg)] text-[var(--color-danger)]'
                    : 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]',
                )}
              >
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1 pt-0.5">
                <h3 className="text-[15px] font-semibold text-[var(--color-text-strong)]">
                  {opts.title}
                </h3>
                {opts.message && (
                  <div className="mt-1 text-[12.5px] leading-relaxed text-[var(--color-text)]">
                    {opts.message}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => settle(false)}
                className="rounded-xl p-1.5 text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]"
                aria-label={t('common.close')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Type-to-confirm */}
            {phraseRequired && (
              <div className="px-6 pt-3.5">
                <label className="text-[11.5px] text-[var(--color-text-dim)]">
                  {t('notifications.phraseRequiredToConfirm', { phrase: phraseRequired })}
                </label>
                <input
                  ref={inputRef}
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && phraseOk) settle(true)
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={phraseRequired}
                  className="mt-1.5 w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-[13px] text-[var(--color-text)] outline-none transition-colors focus:border-[var(--color-danger)]"
                />
              </div>
            )}

            {/* Actions */}
            <div className="mt-4 flex justify-end gap-2 border-t border-[var(--color-border-soft)] bg-[var(--color-surface-2)]/40 px-6 py-3">
              <button
                type="button"
                onClick={() => settle(false)}
                className="rounded-xl px-3.5 py-2 text-[12.5px] font-medium text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)]"
              >
                {opts.cancelLabel ?? t('common.cancel')}
              </button>
              <button
                type="button"
                disabled={!phraseOk}
                onClick={() => settle(true)}
                className={cn(
                  'rounded-xl px-3.5 py-2 text-[12.5px] font-semibold text-white shadow-[var(--shadow-soft)] transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                  tone === 'danger'
                    ? 'bg-[var(--color-danger)] hover:bg-red-600'
                    : 'bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)]',
                )}
              >
                {opts.confirmLabel ?? t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </UndoToastContext.Provider>
    </NotifyContext.Provider>
    </ConfirmContext.Provider>
  )
}
